import React, { useEffect } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormClearErrors,
} from "react-hook-form";
import MyGoogleMap from "../../components/MyGoogleMap";
import { DEFAULT_LAT_LONG } from "../../lib/constants";
import InfoTooltip from "../../components/common/InfoTooltip";
import { useSelector } from "react-redux";
import { IsServiceTypeVendor, tokenDecoder } from "../../lib/utils";
import { IoLocationSharp } from "react-icons/io5";

type LocationFormInputs = {
  longitude: string;
  latitude: string;
  standardDeliveryFee?: number | null;
  address: string;
  cityName: string;
  state: string;
  zipCode: string;
};

interface LocationDetails {
  lat: number;
  lng: number;
  address: string;
  cityName: string;
  state: string;
  zipCode: string;
}

type LocationInformationProps = {
  register: UseFormRegister<LocationFormInputs>;
  errors: FieldErrors<LocationFormInputs>;
  clearErrors: UseFormClearErrors<LocationFormInputs>;
  setValue: any;
  watch: any;
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
};

const LocationSettings: React.FC<LocationInformationProps> = ({
  register,
  errors,
  clearErrors,
  setValue,
  watch,
  setShowAlertOnRouteChange,
}) => {
  const latitude = watch("latitude");
  const longitude = watch("longitude");
  const token = useSelector((state: any) => state.auth.token);
  const decodedToken = tokenDecoder(token);
  const isDistanceRangApplicable = decodedToken.isDistanceRangApplicable;

  const getCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setValue("latitude", latitude);
        setValue("longitude", longitude);
      },
      (error) => {
        console.error("Error getting current location:", error);
      }
    );
  };

  useEffect(() => {
    if (
      latitude === DEFAULT_LAT_LONG.LAT ||
      longitude === DEFAULT_LAT_LONG.LONG
    ) {
      getCurrentLocation();
    }
  }, []);

  const handleOnLocationSelect = (details: LocationDetails) => {
    setShowAlertOnRouteChange(true);
    setValue("longitude", details.lng);
    setValue("latitude", details.lat);
    setValue("address", details.address);
    setValue("cityName", details.cityName);
    setValue("state", details.state);
    setValue("zipCode", details.zipCode);
  };

  const handleFeeChange = (e: any) => {
    setShowAlertOnRouteChange(true);
    const value = e.target.value
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*?)\..*/g, "$1")
      .replace(/^0+(?=\d)/, "")
      .replace(/^(\d*\.?\d{0,2}).*/, "$1");
    setValue("standardDeliveryFee", value);
  };

  return (
    <div className="w-full pb-3">
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
        {/* Left Column - Forms */}
        <div className="space-y-6 lg:col-span-4">
          {/* Location Settings Section */}
          <div className="bg-white p-4 shadow-sm rounded-lg">
            <h1 className="font-helveticaMedium mb-4 text-lg">
              Location Settings
            </h1>
            <div className="flex flex-col">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Latitude Input */}
                <div className="flex flex-col">
                  <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
                    Latitude <span className="text-red-600">*</span>
                    <InfoTooltip content="The geographical coordinate that specifies your store's north-south position. You can either enter it manually or use the map to set it automatically." />
                  </label>
                  <input
                    min={0}
                    type="number"
                    step="any"
                    {...register("latitude", {
                      required: "Latitude is required",
                      valueAsNumber: true,
                      validate: (value) => {
                        const numberValue = Number(value);
                        return !isNaN(numberValue) || "Invalid number";
                      },
                    })}
                    className="focus:outline-customGreenColor mt-1 w-full px-3 py-2 text-sm border border-gray-300 bg-grayBackgroundColor rounded-md shadow-sm"
                    placeholder="Type Latitude here"
                  />
                  {errors.latitude && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.latitude.message}
                    </p>
                  )}
                </div>

                {/* Longitude Input */}
                <div className="flex flex-col">
                  <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
                    Longitude <span className="text-red-600">*</span>
                    <InfoTooltip content="The geographical coordinate that specifies your store's east-west position. You can either enter it manually or use the map to set it automatically." />
                  </label>
                  <input
                    min={0}
                    type="number"
                    step="any"
                    {...register("longitude", {
                      required: "Longitude is required",
                      valueAsNumber: true,
                      validate: (value) => {
                        const numberValue = Number(value);
                        return !isNaN(numberValue) || "Invalid number";
                      },
                    })}
                    className="focus:outline-customGreenColor mt-1 w-full px-3 py-2 text-sm border border-gray-300 bg-grayBackgroundColor rounded-md shadow-sm"
                    placeholder="Type Longitude here"
                  />
                  {errors.longitude && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.longitude.message}
                    </p>
                  )}
                </div>

                {/* Delivery Fee Input */}
                {IsServiceTypeVendor() && !isDistanceRangApplicable ? (
                  <></>
                ) : (
                  <>
                    <div className="md:col-span-2">
                      <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
                        {isDistanceRangApplicable
                          ? "Home Service"
                          : "Standard Delivery Fee"}
                        <InfoTooltip content="Set the base delivery fee for orders from this location. This can be adjusted for specific delivery zones." />
                      </label>
                      <input
                        min={0}
                        type="text"
                        onInput={(e) => handleFeeChange(e)}
                        {...register("standardDeliveryFee", {
                          valueAsNumber: true,
                          validate: (value) => {
                            const numberValue = Number(value);
                            return !isNaN(numberValue) || "Invalid number";
                          },
                        })}
                        readOnly={decodedToken.role !== "ADMIN"}
                        className={`focus:outline-customGreenColor mt-1 w-full px-3 py-2 text-sm border border-gray-300 bg-grayBackgroundColor rounded-md shadow-sm ${
                          decodedToken.role !== "ADMIN"
                            ? "cursor-not-allowed opacity-70"
                            : ""
                        }`}
                        placeholder="Set or update the fee for delivery services"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="bg-white p-4 shadow-sm rounded-lg">
            <h1 className="font-helveticaMedium pb-4 text-lg">Address</h1>
            <div className="flex flex-col space-y-4">
              {/* Address Input */}
              <div>
                <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
                  Address <span className="text-red-600">*</span>
                  <InfoTooltip content="Enter the complete street address for your store location. This will be used for delivery calculations and customer directions." />
                </label>
                <input
                  type="text"
                  {...register("address", { required: true })}
                  className="focus:outline-customGreenColor mt-1 w-full px-3 py-2 text-sm border border-gray-300 bg-grayBackgroundColor rounded-md shadow-sm"
                  placeholder="Set or update the store address"
                />
                {errors.address && (
                  <p className="text-red-500 text-xs mt-1">
                    Address is required.
                  </p>
                )}
              </div>

              {/* City, State, Zip Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* City Input */}
                <div>
                  <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
                    City <span className="text-red-600">*</span>
                    <InfoTooltip content="Enter the city where your store is located." />
                  </label>
                  <input
                    type="text"
                    {...register("cityName", { required: true })}
                    className="focus:outline-customGreenColor mt-1 w-full px-3 py-2 text-sm border border-gray-300 bg-grayBackgroundColor rounded-md shadow-sm"
                    placeholder="City name"
                  />
                  {errors.cityName && (
                    <p className="text-red-500 text-xs mt-1">
                      City name is required.
                    </p>
                  )}
                </div>

                {/* State Input */}
                <div>
                  <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
                    State <span className="text-red-600">*</span>
                    <InfoTooltip content="Enter the state where your store is located." />
                  </label>
                  <input
                    type="text"
                    {...register("state", { required: true })}
                    className="focus:outline-customGreenColor mt-1 w-full px-3 py-2 text-sm border border-gray-300 bg-grayBackgroundColor rounded-md shadow-sm"
                    placeholder="State name"
                  />
                  {errors.state && (
                    <p className="text-red-500 text-xs mt-1">
                      State name is required.
                    </p>
                  )}
                </div>

                {/* Zip Code Input */}
                <div>
                  <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
                    Zip Code <span className="text-red-600">*</span>
                    <InfoTooltip content="Enter the postal code for your store location. This helps in determining delivery zones and shipping calculations." />
                  </label>
                  <input
                    type="text"
                    {...register("zipCode", { required: true })}
                    className="focus:outline-customGreenColor mt-1 w-full px-3 py-2 text-sm border border-gray-300 bg-grayBackgroundColor rounded-md shadow-sm"
                    placeholder="Zip code"
                  />
                  {errors.zipCode && (
                    <p className="text-red-500 text-xs mt-1">
                      Zip code is required.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Map */}
        <div className="lg:col-span-2 h-[400px] lg:h-auto">
          <MyGoogleMap
            onLocationSelect={handleOnLocationSelect}
            initialLocation={{
              lat: latitude || 43.65,
              lng: longitude || -79.38,
            }}
          />

          <div className="bg-white p-4 shadow-sm rounded-lg mt-4">
            <div className="flex flex-col space-y-2">
              <h3 className="text-sm font-helveticaMedium text-gray-600">
                Selected Location<span className="text-red-600">*</span>
              </h3>
              <div className="flex items-start gap-2">
                <IoLocationSharp size={20} className="text-customGreenColor" />
                <div className="flex flex-col">
                  <span className="text-sm text-gray-800 break-all">
                    {watch("address") || "No address selected"}
                  </span>
                  {watch("address") && (
                    <span className="text-xs text-gray-500 break-all">
                      {watch("cityName")}, {watch("state")} {watch("zipCode")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSettings;
